import { useState, useMemo } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Wrench, ChevronDown, ChevronRight, Search, X } from "lucide-react";
import { fuzzyMatch } from "../../lib/searchUtils";
import { formatCurrency as formatCurrencyBase } from "../../lib/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
const EquipmentSettingsTab = ({ equipment, equipmentUnits, equipmentCategories, getAuthHeaders, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    name: "", unit: "", unit_price: "", category: "", description: "", user_defined_price: false
  });

  // Track which categories are expanded (all expanded by default)
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const categories = [...new Set(equipment.map(e => e.category || "Uncategorized"))];
    return categories.reduce((acc, cat) => ({ ...acc, [cat]: true }), {});
  });

  // Group equipment by category (with search filter applied)
  const equipmentByCategory = useMemo(() => {
    const filtered = equipment.filter((e) => {
      if (!search.trim()) return true;
      const searchable = [e.name, e.description, e.category, e.unit].filter(Boolean).join(" ");
      return fuzzyMatch(search, searchable);
    });
    const grouped = {};
    filtered.forEach(e => {
      const cat = e.category || "Uncategorized";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(e);
    });
    // Sort categories alphabetically, but put "Uncategorized" last
    return Object.keys(grouped)
      .sort((a, b) => {
        if (a === "Uncategorized") return 1;
        if (b === "Uncategorized") return -1;
        return a.localeCompare(b);
      })
      .reduce((acc, key) => {
        acc[key] = grouped[key].sort((a, b) => a.name.localeCompare(b.name));
        return acc;
      }, {});
  }, [equipment, search]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const expandAll = () => {
    const allExpanded = Object.keys(equipmentByCategory).reduce((acc, cat) => ({ ...acc, [cat]: true }), {});
    setExpandedCategories(allExpanded);
  };

  const collapseAll = () => {
    const allCollapsed = Object.keys(equipmentByCategory).reduce((acc, cat) => ({ ...acc, [cat]: false }), {});
    setExpandedCategories(allCollapsed);
  };

  const resetForm = () => {
    setForm({ name: "", unit: "", unit_price: "", category: "", description: "", user_defined_price: false });
    setEditingEquipment(null);
  };

  const openEditEquipment = (eq) => {
    setEditingEquipment(eq);
    setForm({
      name: eq.name || "",
      unit: eq.unit || "",
      unit_price: eq.unit_price?.toString() || "",
      category: eq.category || "",
      description: eq.description || "",
      user_defined_price: eq.user_defined_price || false
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        unit_price: form.user_defined_price ? 0 : (parseFloat(form.unit_price) || 0),
        id: editingEquipment?.id || ""
      };
      if (editingEquipment) {
        await apiClient.put(`/settings/equipment/${editingEquipment.id}`, payload);
        toast.success("Equipment updated");
      } else {
        await apiClient.post(`/settings/equipment`, payload);
        toast.success("Equipment added");
      }
      onRefresh();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/settings/equipment/${id}`);
      toast.success("Equipment deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete equipment");
    }
  };

  const formatCurrency = (amount) => formatCurrencyBase(amount, true);

  const categoryCount = Object.keys(equipmentByCategory).length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Equipment</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">{equipment.length} equipment in {categoryCount} categories</p>
        </div>
        <div className="flex items-center gap-2">
          {categoryCount > 1 && (
            <div className="flex items-center gap-1 mr-2">
              <Button variant="ghost" size="sm" onClick={expandAll} className="text-xs h-8">
                Expand All
              </Button>
              <Button variant="ghost" size="sm" onClick={collapseAll} className="text-xs h-8">
                Collapse All
              </Button>
            </div>
          )}
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-equipment-btn"><Plus className="w-4 h-4 mr-2" />Add Equipment</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingEquipment ? "Edit Equipment" : "Add New Equipment"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="form-group">
                  <Label>Equipment Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="e.g., Scaffolding, Mixer" data-testid="equipment-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <Label>Unit</Label>
                    <Select value={form.unit} onValueChange={(v) => setForm({...form, unit: v})}>
                      <SelectTrigger data-testid="equipment-unit-select">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {[...equipmentUnits].sort((a, b) => a.localeCompare(b)).map((unit) => (
                          <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {equipmentUnits.length === 0 && (
                      <p className="text-xs text-muted-foreground mt-1">Add units in Options tab first</p>
                    )}
                  </div>
                  <div className="form-group">
                    <Label>Unit Price ($)</Label>
                    <NumericInput 
                      decimals={2}
                      min={0}
                      value={form.unit_price} 
                      onChange={(e) => setForm({...form, unit_price: e.target.value})} 
                      disabled={form.user_defined_price}
                      placeholder={form.user_defined_price ? "Set per job" : "0.00"} 
                      data-testid="equipment-price-input" 
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2 p-3 bg-muted/30 rounded-lg">
                  <Checkbox 
                    id="equipment-user-defined-price" 
                    checked={form.user_defined_price} 
                    onCheckedChange={(c) => setForm({...form, user_defined_price: c, unit_price: c ? "0" : form.unit_price})}
                    data-testid="equipment-user-defined-price"
                  />
                  <div>
                    <Label htmlFor="equipment-user-defined-price" className="font-medium cursor-pointer">User Defined Price</Label>
                    <p className="text-xs text-muted-foreground">Price will be entered when adding to job estimates</p>
                  </div>
                </div>
                <div className="form-group">
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger data-testid="equipment-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...equipmentCategories].sort((a, b) => a.localeCompare(b)).map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {equipmentCategories.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">Add categories in Options tab first</p>
                  )}
                </div>
                <div className="form-group">
                  <Label>Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={2} data-testid="equipment-description-input" />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-equipment-btn">
                  {editingEquipment ? "Update Equipment" : "Add Equipment"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="p-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search equipment..." className="pl-9 pr-9" data-testid="equipment-search-input" />
            {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
          </div>
        </div>
        {Object.keys(equipmentByCategory).length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Wrench className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No equipment added yet</p>
            <p className="text-sm mt-1">Click &quot;Add Equipment&quot; to get started</p>
          </div>
        ) : (
          <div className="divide-y">
            {Object.entries(equipmentByCategory).map(([category, categoryEquipment]) => (
              <Collapsible 
                key={category} 
                open={expandedCategories[category]} 
                onOpenChange={() => toggleCategory(category)}
              >
                <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 transition-colors" data-testid={`equipment-category-toggle-${category}`}>
                  <div className="flex items-center gap-2">
                    {expandedCategories[category] ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                    <span className="font-semibold">{category}</span>
                    <span className="text-sm text-muted-foreground">({categoryEquipment.length})</span>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header bg-muted/30">
                        <TableHead className="pl-10 w-[40%]">Equipment</TableHead>
                        <TableHead className="w-[15%]">Unit</TableHead>
                        <TableHead className="text-right w-[25%]">Unit Price</TableHead>
                        <TableHead className="text-right w-[20%]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryEquipment.map((eq) => (
                        <TableRow key={eq.id} className="table-row">
                          <TableCell className="pl-10 w-[40%]">
                            <div>
                              <p className="font-medium">{eq.name}</p>
                              {eq.description && <p className="text-sm text-muted-foreground truncate max-w-xs">{eq.description}</p>}
                            </div>
                          </TableCell>
                          <TableCell className="w-[15%]">{eq.unit || "-"}</TableCell>
                          <TableCell className="text-right font-mono w-[25%]">
                            {eq.user_defined_price ? (
                              <span className="text-amber-600 font-medium">Variable</span>
                            ) : (
                              <>{formatCurrency(eq.unit_price)}/{eq.unit || "unit"}</>
                            )}
                          </TableCell>
                          <TableCell className="text-right w-[20%]">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditEquipment(eq)} data-testid={`edit-equipment-${eq.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`delete-equipment-${eq.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Equipment?</AlertDialogTitle>
                                    <AlertDialogDescription>This will permanently remove {eq.name}.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDelete(eq.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EquipmentSettingsTab;
